import { ReactNode, useMemo, useState } from 'react';

import {
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';

import { MatterCard } from './matter-card';
import { PersonalMatterCard } from './personal-matter-card';
import { Icon } from '../../../components/icon';
import { SearchInput } from '../../../components/search-input';
import { TablePagination } from '../../../components/table/table-pagination';
import { Matter } from '@/api/types/backend-dto-types';
import { useUserContext } from '@/Contexts/User';
import { Button } from '@/new/components/shadcn/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/new/components/shadcn/dialog';

const fallbackTableData: Matter[] = [];
const columnHelper = createColumnHelper<Matter>();

// add columns for anything we might want to filter by
// hidden column for case names allows default global filter to search case names
const columns = [
  columnHelper.accessor('name', {}),
  columnHelper.accessor('code', {}),
  columnHelper.accessor('createdDate', {}),
  columnHelper.accessor((row) => row.cases?.map((c) => c.name).join(' ') ?? '', {
    id: 'caseNames',
    enableGlobalFilter: true,
  }),
];

// Helper to get matched subsets for display
const getMatchedSubsets = (matter: Matter, searchFilter: string) => {
  if (!searchFilter || !matter.cases) return [];
  const searchLower = searchFilter.toLowerCase();
  return matter.cases.filter((c) => c.name?.toLowerCase().includes(searchLower));
};

interface MatterListProps {
  matters?: Matter[];
  personalMatter?: Matter | null;
  onboardingButton?: ReactNode;
}

export function MatterList({ matters, personalMatter, onboardingButton }: MatterListProps) {
  const { user } = useUserContext();
  const [globalFilter, setGlobalFilter] = useState('');

  const nonDraftMatters = useMemo(() => matters?.filter((matter) => !matter.isDraft) ?? fallbackTableData, [matters]);
  const draftMatters = useMemo(
    () => matters?.filter((matter) => matter.isDraft && matter.createdById === user?.id) ?? [],
    [matters],
  );

  // Check if personal matter should be shown (matches search filter if one is active)
  const showPersonalMatter = useMemo(() => {
    if (!personalMatter) return false;
    if (!globalFilter) return true;
    return (
      personalMatter.name?.toLowerCase().includes(globalFilter.toLowerCase()) ||
      personalMatter.cases?.some((c) => c.name?.toLowerCase().includes(globalFilter.toLowerCase()))
    );
  }, [personalMatter, globalFilter]);

  // using tanstack table so we have things like sorting, pagination, filtering etc. easily
  const table = useReactTable({
    data: nonDraftMatters,
    columns,
    initialState: {
      sorting: [
        {
          id: 'createdDate',
          desc: true,
        },
      ],
      pagination: {
        pageSize: 30,
      },
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onGlobalFilterChange: setGlobalFilter,
    state: {
      globalFilter,
    },
  });

  return (
    <div>
      <div className="my-6 flex w-full flex-wrap items-center gap-4">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-3">
          <SearchInput
            placeholder="Search matters..."
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.target.value)}
            className="min-w-48 flex-1 sm:max-w-sm"
          />

          {draftMatters && draftMatters.length > 0 && (
            <Dialog>
              <DialogTrigger asChild>
                <Button className="w-full sm:w-auto">
                  <Icon className="lucide--file-pen text-slate-600" />
                  <span>Drafts ({draftMatters.length})</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-4xl">
                <DialogHeader>
                  <DialogTitle>
                    <div className="flex items-center gap-2">
                      <Icon className="lucide--file-pen text-slate-600" />
                      <span>Draft Matters</span>
                      <span className="text-xs font-normal text-gray-500">({draftMatters.length})</span>
                    </div>
                  </DialogTitle>
                  <DialogDescription>Continue where you left off to finish creating these matters.</DialogDescription>
                </DialogHeader>
                <div className="grid grid-cols-[repeat(auto-fit,minmax(20rem,1fr))] gap-4">
                  {draftMatters.map((matter) => (
                    <MatterCard key={matter.id} matter={matter} isDraft={true} />
                  ))}
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {onboardingButton && <div className="ml-auto flex">{onboardingButton}</div>}
      </div>

      <div className="my-6 grid grid-cols-[repeat(auto-fit,minmax(24rem,1fr))] gap-4">
        {showPersonalMatter && personalMatter && <PersonalMatterCard matter={personalMatter} />}
        {table.getRowModel().rows.map((row) => {
          const matchedCases = globalFilter ? getMatchedSubsets(row.original, globalFilter) : [];
          return (
            <MatterCard
              key={row.original.id}
              matter={row.original}
              isDraft={false}
              matchedCases={matchedCases}
              searchFilter={globalFilter}
            />
          );
        })}
      </div>
      <TablePagination table={table} plural="matters" />
    </div>
  );
}
